import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  OnChanges,
  signal,
  type SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { VatAmountCalculator } from '@zaoblako/shared/common/classes/vat.amount.calculator';
import { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import {
  DocumentCreateDto,
  type DocumentCreateInterface
} from '@zaoblako/shared/modules/document/dtos/document/document-create.dto';
import type { DocumentFindInterface } from '@zaoblako/shared/modules/document/dtos/document/document-find.dto';
import { DocumentCalculationMethodEnum } from '@zaoblako/shared/modules/document/enums/document.calculation.method.enum';
import { DocumentKeyEnum } from '@zaoblako/shared/modules/document/enums/document.key.enum';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { DocumentRegistryService } from '@zaoblako/web/core/services/document/document-registry.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { DocumentHistoryComponent } from '@zaoblako/web/modules/document/dumbs/document-form-sidebar/components/document-history/document-history.component';
import { DocumentSplitWidgetComponent } from '@zaoblako/web/modules/document/dumbs/document-form-sidebar/components/document-split-widget/document-split-widget.component';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { GoBackButtonComponent } from '@zaoblako/web/shared/components/go-back-button/go-back-button.component';
import { BankDetailsAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/bank-details-auto-complete/bank-details-auto-complete.component';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { ContractSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contract-single-select/contract-single-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { CurrencySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-single-select/currency-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { DocumentTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/document-type-single-select/document-type-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { OperationTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/operation-type-single-select/operation-type-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import moment from 'moment-timezone';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessagesModule } from 'primeng/messages';
import { Subscription } from 'rxjs';
import { isNil, omit } from 'lodash';

interface RegistryFormDto extends DocumentCreateInterface {
  code?: string;
}

@Component({
  standalone: true,
  selector: 'crm-document-form',
  templateUrl: './document-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    DatepickerComponent,
    JoinPipe,
    InputNumberModule,
    FormsModule,
    SingleSelectComponent,
    ContragentAutoCompleteComponent,
    DirectionSingleSelectComponent,
    EditorComponent,
    CheckboxModule,
    ButtonComponent,
    GoBackButtonComponent,
    RouterModule,
    DocumentHistoryComponent,
    BankDetailsAutoCompleteComponent,
    DealAutoCompleteComponent,
    DocumentSplitWidgetComponent,
    UnitSingleSelectComponent,
    OperationTypeSingleSelectComponent,
    FilialSingleSelectComponent,
    DocumentTypeSingleSelectComponent,
    CostCenterSingleSelectComponent,
    MessagesModule,
    CurrencySingleSelectComponent,
    ContractSingleSelectComponent,
    CompanySingleSelectComponent,
    DialogModule,
    SpinnerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentFormComponent extends BaseFormComponent<DocumentCreateInterface> implements OnInit, OnDestroy, OnChanges {
  @Input() public document = <RegistryFormDto>{};
  public dto = DocumentCreateDto;

  @Input() public readonly index: number;
  @Input() public directions: DirectionFindInterface[] = [];
  @Input() public nds: NdsFindInterface[] = [];
  @Input() public bankDetails: BankDetailsFindInterface[] = [];

  @Input() public canCreateBasedOn = false;
  @Input() public canCreateCopyOn = false;
  @Input() public canRepeatOn = false;
  @Input() public canCreateTemplate = false;
  @Input() public isModal = false;
  @Input() public canWrite = false;

  public loadingPdf = false;

  public currencies: CurrencyFindInterface[] = [];

  public isShowRepeatDialog = signal(false);
  public loadingRepeat = signal(false);
  public months = signal(0);

  @Output() public bankDetailsSearch = new EventEmitter<string>();

  @Output() public createBasedOnClick = new EventEmitter<void>();

  private formSubscription$: Subscription = new Subscription();
  private repeatTimeoutIds: ReturnType<typeof setTimeout>[] = [];

  public get DocumentCalculationMethodEnum() {
    return DocumentCalculationMethodEnum;
  }

  public get DocumentKeyEnum() {
    return DocumentKeyEnum;
  }

  public get access() {
    return {
      canReadDeal: this.accessService.finance.dealRights.canReadDeal,
      canWriteTemplate: this.accessService.finance.documentRights.canWriteTemplate,
      canLockDocuments: this.accessService.finance.documentPermissions.canLockDocuments
    };
  }

  public constructor(
    private readonly documentRegistryService: DocumentRegistryService,
    private readonly accessService: AccessService,
    public readonly globalService: GlobalService,
    private readonly formStoreService: FormStoreService,
    private readonly companyService: CompanyService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService
  ) {
    super();
  }

  public ngOnInit(): void {
    if (!this.document.date) {
      this.document.date = moment().toDate();
    }
    if (this.document.company) {
      this.changeCompany();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.document?.currentValue?.company?.id !== changes.document?.previousValue?.company?.id) {
      this.changeCompany();
    }
  }

  public ngOnDestroy(): void {
    this.clearRepeatTimeouts();
    this.formSubscription$?.unsubscribe();
  }

  public onSave(entity: RegistryFormDto) {
    // Edit: strip source — DocumentCreateDto only allows FRM/BALANCE_SHEET (create).
    // Update API uses DocumentFormDto and does not change source.
    const payload: RegistryFormDto = this.document.code
      ? (omit(entity, ['source']) as RegistryFormDto)
      : entity;

    const { valid } = this.checkValidate(payload);
    if (!valid) {
      this.errorService.addCustomError(
        'Ошибка валидации',
        Object.keys(this.errors || {})
          .map((key) => `[${key}]: ${this.errors[key]};`)
          .join('\n')
      );
      this.cdRef.markForCheck();
      return;
    }

    const requiredAnalyticalFeatures = this.globalService.globalInfo?.requiredAnalyticalFeatures;
    if (
      (requiredAnalyticalFeatures?.directionsRequired && !this.document.direction) ||
      (requiredAnalyticalFeatures?.unitsRequired && !this.document.unit) ||
      (requiredAnalyticalFeatures?.filialsRequired && !this.document.filial)
    ) {
      this.errors = Object.create({});

      if (requiredAnalyticalFeatures?.directionsRequired && !this.document.direction) {
        this.errors.direction = 'Бюджетная статья не выбрана' as any;
      }
      if (requiredAnalyticalFeatures?.unitsRequired && !this.document.unit) {
        this.errors.unit = 'Бюджетная единица не выбрана' as any;
      }
      if (requiredAnalyticalFeatures?.filialsRequired && !this.document.filial) {
        this.errors.filial = 'Филиал не выбран' as any;
      }
      this.cdRef.markForCheck();
      return;
    }

    if (this.document.documentType?.sync1c) {
      this.errors = null;
      this.save.emit(payload);
    } else {
      super.onSave(payload);
    }
  }

  public changeValue() {
    if (this.document.nds?.amount !== null && this.document.nds?.amount !== undefined) {
      this.document.vatAmount = VatAmountCalculator.getInstance().getVatAmount(
        this.document.amount || 0,
        this.document.nds?.amount || 0
      );
    }
  }

  public createCopyDocument(data: { copyId: string }) {
    this.formStoreService.saveNext(this.document, this.index);

    const result = this.formStoreService.addForm(TypeFormEnum.DOCUMENT, {
      headerText: 'Создание документа',
      isNotShowRedirectInCreate: true,
      data
    });

    this.formSubscription$.add(
      result.onSave.subscribe(() => this.router.navigate(['/document/list']).catch(console.error))
    );
  }

  public createRepeatDocuments(): void {
    if (!this.months()) {
      return;
    }

    this.clearRepeatTimeouts();
    this.loadingRepeat.set(true);

    const sourceDate = this.document.date;
    const monthsCount = this.months();
    let finallyNum = 0;
    let successNum = 0;

    for (let i = 0; i < monthsCount; i += 1) {
      const timeoutId = setTimeout(() => {
        const payload: DocumentCreateInterface = this.buildRepeatPayload(
          moment(sourceDate)
            .add(i + 1, 'month')
            .toDate()
        );

        this.formSubscription$.add(
          this.documentRegistryService.create<DocumentFindInterface>(payload).subscribe({
            next: () => {
              finallyNum++;
              successNum++;
              this.finishRepeatIfDone(finallyNum, successNum, monthsCount);
            },
            error: () => {
              finallyNum++;
              this.finishRepeatIfDone(finallyNum, successNum, monthsCount);
            }
          })
        );
      }, 300);

      this.repeatTimeoutIds.push(timeoutId);
    }
  }

  private clearRepeatTimeouts(): void {
    this.repeatTimeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    this.repeatTimeoutIds = [];
  }

  private finishRepeatIfDone(finallyNum: number, successNum: number, monthsCount: number): void {
    if (finallyNum !== monthsCount) {
      return;
    }

    if (successNum === monthsCount) {
      this.errorService.addSuccessMessage('Документы созданы');
    } else if (!successNum) {
      this.errorService.addCustomError('Документы не были созданы');
    } else {
      this.errorService.addSuccessMessage('Документы были частично созданы');
    }

    this.loadingRepeat.set(false);
    this.isShowRepeatDialog.set(false);
  }

  private buildRepeatPayload(date: Date): DocumentCreateInterface {
    const payload: DocumentCreateInterface & { rate?: number; } = {
      date,
      amount: this.document.amount,
      vatAmount: this.document.vatAmount ?? 0,
      commentary: this.document.commentary,
      locked: this.document.locked,
      company: this.document.company,
      contragent: this.document.contragent,
      operationType: this.document.operationType,
      filial: this.document.filial,
      unit: this.document.unit,
      direction: this.document.direction,
      deal: this.document.deal,
      nds: this.document.nds,
      bankDetails: this.document.bankDetails,
      contract: this.document.contract,
      costCenter: this.document.costCenter,
      currency: this.document.currency,
      documentType: this.document.documentType
    };

    if (!isNil(this.document['rate'])) {
      payload.rate = this.document['rate'];
    }

    return payload;
  }

  public createTemplate(data: { documentId: string }) {
    this.formStoreService.saveNext(this.document, this.index);

    const result = this.formStoreService.addForm(TypeFormEnum.DOCUMENT_TEMPLATE, {
      headerText: 'Новый шаблон',
      isNotShowRedirectInCreate: true,
      data
    });

    this.formSubscription$.add(
      result.onSave.subscribe(() => this.router.navigate(['/document/template']).catch(console.error))
    );
  }

  public isDocumentRelated(document: RegistryFormDto) {
    return (
      (document as DocumentFindInterface).groupCodes?.length &&
      (document as DocumentFindInterface).groupCodes.every((groupCode) => groupCode !== document?.code)
    );
  }

  public loadToPdf() {
    this.loadingPdf = true;
    this.cdRef.markForCheck();

    this.documentRegistryService.downloadOneAsPdf(this.document?.code).subscribe({
      next: (data) => {
        const file = new Blob([data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, '_blank');
        this.loadingPdf = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.loadingPdf = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public changeCompany() {
    const oldCurrency = this.document.currency;

    if (this.document.company?.id) {
      this.companyService
        .findById<CompanyFindInterface>(this.document.company.id, {
          select: [
            'id',
            'currencies'
          ]
        })
        .subscribe({
          next: (company) => {
            this.currencies = company.currencies;
            this.document.currency = this.currencies.find((currency) => currency.id === oldCurrency?.id || currency.isDefault);
            this.document['rate'] = this.document.currency?.latestRate?.rate;
            this.cdRef.markForCheck();
          },
          error: () => {
            this.currencies = [];
            this.document.currency = null;
            this.cdRef.markForCheck();
          }
        });
    } else {
      this.currencies = [];
      this.document.currency = null;
      this.cdRef.markForCheck();
    }


    // this.currencies = [];
    // this.document.currency = null;

    // if (this.document.company?.currencies?.length) {
    //   this.currencies = this.document.company.currencies;
    //   setTimeout(() => {
    //     this.document.currency = this.currencies.find((currency) => currency.id === oldCurrency?.id || currency.isDefault);
    //     this.document['rate'] = this.document.currency?.latestRate?.rate;
    //     this.cdRef.markForCheck();
    //   });
    // }
  }

  public changeAmount(amount: string | number) {
    this.document.amount = Number(amount);
  }
}
